import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import apiUtils from '../../global/utils/api';

import {
  handleCreateFulfilled
  , handleFetchSinglePending
  , handleFetchSingleFulfilled
  , handleFetchSingleFromListFulfilled
  , handleFetchSingleRejected
  , handleFetchListPending
  , handleFetchListFulfilled
  , handleFetchListRejected
  , handleMutationPending
  , handleMutationFulfilled
  , handleMutationRejected
  , handleDeletePending
  , handleDeleteFulfilled
  , handleDeleteRejected
  , shouldFetch
  , INITIAL_STATE
  , handleInvalidateQuery
  , handleInvalidateQueries
  , handleAddSingleToList
  , handleAddManyToList
} from '../../global/utils/storeUtils';


// First define all API calls for constant

// define and export the strings for the different specific constant endpoints once here because the idea of using strings in the component gives me hives.
// we'll catch for these strings on the server side and apply the correct permissions to the query.
// these are passed in to the constantService hooks at the component level as the endpoint argument.
// export const myExampleEndpoint = 'logged-in';
/**
 * The functions below, called thunks, allow us to perform async logic. They
 * can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
 * will call the thunk with the `dispatch` function as the first argument. Async
 * code can then be executed and other actions can be dispatched. Thunks are
 * typically used to make async requests.
 * 
 * In practice we won't dispatch these directly, they will be dispatched by constantService which has a nicer api built on hooks.
 */

// CREATE
export const sendCreateConstant = createAsyncThunk(
  'constant/sendCreate'
  , async (newConstant) => {
    const endpoint = `/api/constants`;
    const response = await apiUtils.callAPI(endpoint, 'POST', newConstant);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// READ
export const fetchSingleConstant = createAsyncThunk(
  'constant/fetchSingle'
  , async (id) => {
    const endpoint = `/api/constants/${id}`;
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);
export const fetchConstantList = createAsyncThunk(
  'constant/fetchList' // this is the action name that will show up in the console logger.
  , async (listArgs) => {
    const endpoint = `/api/constants${listArgs}`;
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// for each resource we can add as many endpoints as we want in this format and we only need two actions to handle them.
// this will hit the same endpoint as the list version, but the store will handle the returned array and access the single item in it.
export const fetchSingleConstantAtEndpoint = createAsyncThunk(
  'constant/fetchSingleWithFilter' // this is the action name that will show up in the console logger.
  , async (query) => {
    const endpoint = `/api/constants${query}` // example: `/api/constants/logged-in?${queryString}`
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);
export const fetchConstantListAtEndpoint = createAsyncThunk(
  'constant/fetchListWithFilter' // this is the action name that will show up in the console logger.
  , async (query) => {
    const endpoint = `/api/constants${query}`; // example: `/api/constants/logged-in?${queryString}`
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// UPDATE
export const sendUpdateConstant = createAsyncThunk(
  'constant/sendUpdate'
  , async ({ _id, ...updates }) => {
    const endpoint = `/api/constants/${_id}`;
    const response = await apiUtils.callAPI(endpoint, 'PUT', updates);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// DELETE
export const sendDeleteConstant = createAsyncThunk(
  'constant/sendDelete'
  , async (id) => {
    const endpoint = `/api/constants/${id}`;
    const response = await apiUtils.callAPI(endpoint, 'DELETE');
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// next define the store's initial state, all of our store utils rely on a specific state shape, so use the constant
const initialState = { ...INITIAL_STATE };

// define the constantSlice. This is a combination of actions and reducers. More info: https://redux-toolkit.js.org/api/createSlice
export const constantSlice = createSlice({
  name: 'constant'
  , initialState
  /**
   * The `reducers` field lets us define reducers and generate associated actions.
   * Unlike the selectors defined at the bottom of this file, reducers only have access
   * to this specific reducer and not the entire store.
   * 
   * Again, we will not dispatch these directly, they will be dispatched by constantService.
   */
  , reducers: {
    invalidateQuery: handleInvalidateQuery
    , invalidateQueries: handleInvalidateQueries
    , addConstantToList: handleAddSingleToList
    , addConstantsToList: handleAddManyToList
  }

  /**
   * The `extraReducers` field lets the slice handle actions defined elsewhere,
   * including actions generated by createAsyncThunk or in other slices.
   * We'll use them to track our server request status.
   * 
   * We'll add a case for each API call defined at the top of the file to dictate
   * what happens during each API call lifecycle.
   */
  , extraReducers: (builder) => {
    builder
      // CREATE
      .addCase(sendCreateConstant.fulfilled, handleCreateFulfilled)

      // READ
      .addCase(fetchSingleConstant.pending, handleFetchSinglePending)
      .addCase(fetchSingleConstant.fulfilled, handleFetchSingleFulfilled)
      .addCase(fetchSingleConstant.rejected, handleFetchSingleRejected)
      .addCase(fetchConstantList.pending, handleFetchListPending)
      // because lists are returned from the server named for their resource, we need to pass a `listKey` so the util can properly handle the response
      .addCase(fetchConstantList.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'constants'))
      .addCase(fetchConstantList.rejected, handleFetchListRejected)

      // permission protected single fetches
      .addCase(fetchSingleConstantAtEndpoint.pending, handleFetchSinglePending)
      // these endpoints return named lists, we need to pass a `listKey` so the util can properly handle the response
      .addCase(fetchSingleConstantAtEndpoint.fulfilled, (state, action) => handleFetchSingleFromListFulfilled(state, action, 'constants'))
      .addCase(fetchSingleConstantAtEndpoint.rejected, handleFetchSingleRejected)
      // permission protected list fetches
      .addCase(fetchConstantListAtEndpoint.pending, handleFetchListPending)
      .addCase(fetchConstantListAtEndpoint.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'constants'))
      .addCase(fetchConstantListAtEndpoint.rejected, handleFetchListRejected)

      // UPDATE
      .addCase(sendUpdateConstant.pending, handleMutationPending)
      .addCase(sendUpdateConstant.fulfilled, handleMutationFulfilled)
      .addCase(sendUpdateConstant.rejected, handleMutationRejected)
      // .addCase(sendUpdateConstant.fulfilled, (state, action) => handleMutationFulfilled(state, action, (newState, action) => {
      //   // by passing this optional callback we now have access to the new state if we want to do something else with it, this works for all reducer handlers
      // }))

      // DELETE
      .addCase(sendDeleteConstant.pending, handleDeletePending)
      .addCase(sendDeleteConstant.fulfilled, handleDeleteFulfilled)
      .addCase(sendDeleteConstant.rejected, handleDeleteRejected)
  }
});

// export the actions for the reducers defined above
export const { invalidateQuery, invalidateQueries, addConstantToList, addConstantsToList } = constantSlice.actions;


// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
// accepts an optional listFetch action so we can use it for other list fetches, defaults to fetchConstantList
export const fetchListIfNeeded = (queryKey, listFetch = fetchConstantList) => (dispatch, getState) => {
  const constantQuery = getState().constant.listQueries[queryKey];
  if(shouldFetch(constantQuery)) {
    // console.log('Fetching constant list', queryKey);
    dispatch(listFetch(queryKey));
  } else {
    // console.log('No need to fetch, fresh query in cache');
  }
};

// accepts an optional singleFetch action so we can use it for other single fetches, defaults to fetchSingleConstant
export const fetchSingleIfNeeded = (id, singleFetch = fetchSingleConstant) => (dispatch, getState) => {
  const constantQuery = getState().constant.singleQueries[id];
  if(shouldFetch(constantQuery)) {
    dispatch(singleFetch(id));
  } else {
    // console.log('No need to fetch, fresh query in cache');
  }
}

export default constantSlice.reducer;
